import React, { useEffect } from "react";
import styled from "styled-components/macro";
import { topPadding, bottomPadding } from "../../tools/device";
import { useToggle } from "react-use";
import { Box, Stack, Button } from "@mui/material";
import MammothWithBubble, { MammothVariant } from "../tutorial/onboarding/MammothWithBubble";
import { Trans } from "react-i18next";
import { useNavigate } from "react-router";
import { useViewerQuery } from "../../queries/viewerQuery";
import { MAIN, ONBOARDING, VERIFY } from "../RootRouter.config";
import RegistrationAgreeText from "./RegistrationAgreeText";
import { AuthOptions } from "./AuthOptions";
import useOnboardingCompleted from "hooks/useOnboardingCompleted";
import { StepScreenId } from "root/tutorial/onboarding/onboardingTypes";
import { getStepHash } from "root/tutorial/onboarding/OnboardingRedirect";
import { useSubscribe } from "root/profile/subscription/subscriptionHooks";
import { useUserSale } from "../../queries/users/userSaleQuery";

const Wrap = styled(Stack)`
  display: flex;
  height: 100dvh;
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
  justify-content: space-between;
  flex-direction: column;
  background-color: ${({ theme }) => theme.duo.palette.blueVeryLight};
  padding: ${topPadding + 30}px 0 ${bottomPadding + 22}px;
  gap: 10px;
`;
const InnerWrap = styled(Box)`
  padding: 0 30px;
`;
const MammothWrap = styled.div`
  margin-left: auto;
  margin-right: auto;
`;

type Props = {};

export const LateRegistrationPage: React.FC<Props> = () => {
  const [isEmailOpen, toggleEmailOpen] = useToggle(false);
  const navigate = useNavigate();
  const subscribe = useSubscribe();
  const { isGuest, isLoading, viewer } = useViewerQuery();
  const { isLoading: isUserSaleLoading } = useUserSale();
  const hasOnboardingCompleted = useOnboardingCompleted();
  const targetUrl = hasOnboardingCompleted ? MAIN.url() : ONBOARDING.url() + getStepHash(StepScreenId.FIRST_PAYWALL);
  const userFromOnboardingPaywall = !hasOnboardingCompleted; // The user clicked on "subscribe" to get here, so we need to subscribe them

  const notNow = () => {
    navigate(targetUrl);
    userFromOnboardingPaywall && subscribe();
  };

  useEffect(() => {
    if (isGuest) return;

    // Wait for userSale query to load before subscribing to ensure sale prices are shown
    if (userFromOnboardingPaywall && isUserSaleLoading) return;

    if (viewer?.email && !viewer?.emailVerified && !userFromOnboardingPaywall) {
      navigate(VERIFY.url(), { replace: true });
      return;
    }
    navigate(targetUrl, { replace: true });
    userFromOnboardingPaywall && subscribe();
  }, [isGuest, navigate, userFromOnboardingPaywall, subscribe, targetUrl, viewer, isUserSaleLoading]);

  if (isLoading || !isGuest) {
    return null;
  }

  return (
    <Wrap>
      <div />
      <MammothWrap>
        <MammothWithBubble variant={MammothVariant.Large}>
          <Trans>Sign up so you don't lose your progress and can log in from any device.</Trans>
        </MammothWithBubble>
      </MammothWrap>
      <InnerWrap>
        <AuthOptions isEmailOpen={isEmailOpen} areLoginOptionsOpen onEmailToggle={toggleEmailOpen} />

        <Box sx={{ marginTop: 2 }}>
          <RegistrationAgreeText />
        </Box>

        {!isEmailOpen && !userFromOnboardingPaywall && (
          <Box sx={{ marginTop: 2 }}>
            <Button variant="text" color="inherit" fullWidth onClick={notNow}>
              <Trans>Not now</Trans>
            </Button>
          </Box>
        )}
      </InnerWrap>
    </Wrap>
  );
};
